/**
 * Tiny achievement system. localStorage-backed. Eight unlocks; once earned,
 * never re-toasted. Pure client. No server contract.
 *
 * Public surface:
 *   - check(ctx): call from gameplay code with a snapshot of relevant signals;
 *     fires `onUnlock(id, title)` for any new unlocks this tick.
 *   - allEarned(): for UI surfacing if/when an achievement panel ships.
 */

const KEY = "aether.achievements";

export interface Achievement {
  id: string;
  title: string;
  detail: string;
  /** Optional target value used by the progress-bar renderer. */
  target?: number;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: "first_pass", title: "First Catch", detail: "Caught your first ring.", target: 1 },
  { id: "streak_5", title: "Finding the Lane", detail: "Held a streak of 5.", target: 5 },
  { id: "streak_25", title: "In the Pocket", detail: "Held a streak of 25.", target: 25 },
  { id: "score_1k", title: "Warm Up", detail: "Scored 1,000 in a single run.", target: 1000 },
  { id: "score_5k", title: "Drifter", detail: "Scored 5,000 in a single run.", target: 5000 },
  { id: "score_10k", title: "Lattice Walker", detail: "Scored 10,000 in a single run.", target: 10000 },
  { id: "biome_master", title: "Cartographer", detail: "Visited all four biomes.", target: 4 },
  { id: "no_miss_min", title: "Clean Minute", detail: "60 seconds of running without a miss.", target: 60 },
];

/** Look up best-effort progress per achievement id from localStorage state. */
export function achievementProgress(id: string): number {
  try {
    if (id === "score_1k" || id === "score_5k" || id === "score_10k") {
      const bests = JSON.parse(localStorage.getItem("aether.bests") || "{}");
      return Math.max(0, ...Object.values(bests).map((v) => Number(v) || 0));
    }
    if (id === "biome_master") {
      const arr = JSON.parse(localStorage.getItem("aether.biomes_seen") || "[]");
      return Array.isArray(arr) ? arr.length : 0;
    }
    if (id === "streak_5" || id === "streak_25") {
      return parseInt(localStorage.getItem("aether.lifetime_streak_best") || "0", 10) || 0;
    }
    if (id === "no_miss_min") {
      return parseInt(localStorage.getItem("aether.no_miss_best") || "0", 10) || 0;
    }
    if (id === "first_pass") {
      // Earned-or-not is binary; the existing earned-set check is enough.
      const earned = JSON.parse(localStorage.getItem("aether.achievements") || "{}");
      return earned.first_pass ? 1 : 0;
    }
  } catch { /* swallow */ }
  return 0;
}

export interface CheckCtx {
  score: number;
  streak: number;
  passCount: number;
  noMissSeconds: number;
  biomesVisited: Set<number>;
}

function load(): Record<string, true> {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function save(m: Record<string, true>) {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* quota */
  }
}

export class Achievements {
  private earned = load();
  private onUnlock: (a: Achievement) => void;

  constructor(onUnlock: (a: Achievement) => void) {
    this.onUnlock = onUnlock;
  }

  allEarned(): string[] {
    return Object.keys(this.earned);
  }

  check(ctx: CheckCtx) {
    const unlock = (id: string) => {
      if (this.earned[id]) return;
      const a = ACHIEVEMENTS.find((x) => x.id === id);
      if (!a) return;
      this.earned[id] = true;
      save(this.earned);
      this.onUnlock(a);
    };

    if (ctx.passCount >= 1) unlock("first_pass");
    if (ctx.streak >= 5) unlock("streak_5");
    if (ctx.streak >= 25) unlock("streak_25");
    if (ctx.score >= 1000) unlock("score_1k");
    if (ctx.score >= 5000) unlock("score_5k");
    if (ctx.score >= 10000) unlock("score_10k");
    if (ctx.biomesVisited.size >= 4) unlock("biome_master");
    if (ctx.noMissSeconds >= 60) unlock("no_miss_min");
  }
}

/**
 * Renders a transient toast in the top-center for 3s on each unlock.
 * The DOM element is created on demand and pooled.
 */
export function makeToaster(): (a: Achievement) => void {
  let host = document.querySelector<HTMLDivElement>(".achievement-toasts");
  if (!host) {
    host = document.createElement("div");
    host.className = "achievement-toasts";
    document.body.appendChild(host);
  }
  return (a: Achievement) => {
    const el = document.createElement("div");
    el.className = "achievement-toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.innerHTML = `<strong>${escapeHtml(a.title)}</strong><span>${escapeHtml(a.detail)}</span>`;
    host!.appendChild(el);
    requestAnimationFrame(() => el.classList.add("in"));
    setTimeout(() => {
      el.classList.remove("in");
      el.classList.add("out");
      setTimeout(() => el.remove(), 350);
    }, 3000);
  };
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
